import { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import ChatPage from './pages/ChatPage';
import AdminTiers from './pages/AdminTiers';
import PricingPage from './pages/PricingPage';
import ContactUsToUpgradePage from './pages/ContactUsToUpgradePage';
import { ROUTES } from './resources/routes-constants';
import ScrollToTop from './components/common/ScrollToTop';
import JobDescriptionPage from './pages/JobDescriptionPage';
import GlobalAuthWrapper from './components/common/GlobalAuthWrapper';
import SpinnerLoader from './components/common/SpinnerLoader';
import { AnalyticsWrapper } from './components/common/AnalyticsWrapper';

// Lazy load components for better performance
const Privacy = lazy(() => import('./pages/Privacy'));
const HomePage = lazy(() => import('./pages/HomePage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const TermsService = lazy(() => import('./pages/TermsService'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DashboardEmpty = lazy(() => import('./pages/DashboardEmpty'));
const CompanyProfilePage = lazy(() => import('./pages/CompanyProfilePage'));
const EditCompanyProfilePage = lazy(() => import('./pages/EditCompanyProfilePage'));
const AdminUserManagement = lazy(() => import('./pages/AdminUserManagement'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const QualifiedCandidatesPage = lazy(() => import('./pages/QualifiedCandidatesPage'));
const OutreachPage = lazy(() => import('./pages/OutreachPage'));

// ErrorBoundary component to wrap Suspense
const ErrorBoundary = ({ children }: any) => {
  return (
    <Suspense fallback={<div className="summary-loader"><SpinnerLoader /></div>}>
      {children}
    </Suspense>
  );
};

// Define routes with ScrollToTop and ErrorBoundary wrapping each element
const routes = [
  {
    path: '/',
    element: <GlobalAuthWrapper />,
    children: [
      { path: '*', element: <NotFoundPage /> },
      { path: ROUTES.HOMEPAGE_ROUTE, element: <HomePage /> },
      { path: ROUTES.DASHBOARD_PAGE_ROUTE, element: <DashboardPage /> },
      { path: ROUTES.SIGN_UP_ROUTE, element: <SignUpPage /> },
      { path: ROUTES.COMPANY_PROFILE_ROUTE, element: <CompanyProfilePage /> },
      { path: ROUTES.DASHBOARD_EMPTY_ROUTE, element: <DashboardEmpty /> },
      { path: ROUTES.PRIVACY_ROUTE, element: <Privacy /> },
      { path: ROUTES.TERMS_SERVICE_ROUTE, element: <TermsService /> },
      { path: ROUTES.EDIT_COMPANY_PROFILE_ROUTE, element: <EditCompanyProfilePage /> },
      { path: ROUTES.ADMIN_USER_MANAGEMENT, element: <AdminUserManagement /> },
      { path: ROUTES.ADMIN_DASHBOARD, element: <AdminDashboard /> },
      { path: ROUTES.ADMIN_TIER_MANAGEMENT, element: <AdminTiers /> },
      { path: ROUTES.ADMIN_ANALYTICS, element: <AdminAnalytics /> },
      { path: ROUTES.CHAT_PAGE_ROUTE, element: <ChatPage /> },
      { path: ROUTES.JOB_DESCRIPTION, element: <JobDescriptionPage /> },
      { path: ROUTES.IDEALIZE_CANDIDATE_PAGE, element: <JobDescriptionPage /> },
      { path: ROUTES.JOB_DESCRIPTION_SUMMARY, element: <JobDescriptionPage /> },
      { path: ROUTES.CANDIDATE_SELECTION, element: <JobDescriptionPage /> },
      { path: ROUTES.QUALIFIED_CANDIDATES_PAGE, element: <QualifiedCandidatesPage /> },
      { path: ROUTES.OUTREACH_PAGE, element: <OutreachPage /> },
      { path: ROUTES.PRICING_PAGE_ROUTE, element: <PricingPage /> },
      { path: ROUTES.CONTACT_US_TO_UPGRADE_ROUTE, element: <ContactUsToUpgradePage /> },
    ].map(route => ({
      ...route,
      element: (
        <>
          <ScrollToTop />
          <AnalyticsWrapper />
          <ErrorBoundary>{route.element}</ErrorBoundary>
        </>
      ),
    }))
  }
];

// Create the router instance
const router = createBrowserRouter(routes);

const RootComponent = () => {
  return <RouterProvider router={router} />;
};

export default RootComponent;